import { useCallback, useEffect, useState } from 'react';
import { AppWindow, Bookmark, Check, Pencil, Trash2, Video, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { NoCargo } from '../NoCargo';
import { useFallo } from '../../lib/useFallo';
import { fmtElapsed } from '../../lib/share/peer';
import { emitRecordingChange } from '../../lib/rec/store';
import { claimLabel, type MineResponse, type RecordingDTO } from '../../lib/rec/types';
import { fmtDate } from './RecordingCard';
import { useRec } from './RecProvider';

/**
 * "My recordings" (spec §4.12.5, ProfileMenu): lista, "Unfinished recording (3:40) — Recover /
 * Discard" para las filas `uploading` (Recover solo si esta máquina guardó cuántas partes subió),
 * renombrar, borrar, Keep, abrir.
 */
export function MyRecordings({ onClose }: { onClose: () => void }) {
  const { t, lang } = useI18n();
  const r = useRec();
  const [rows, setRows] = useState<RecordingDTO[] | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const { fallo, marcar, limpiar } = useFallo();

  const load = useCallback(() => {
    api.get<MineResponse>('/api/recordings/mine').then((res) => { limpiar(); setRows(res.rows); }).catch(marcar);
  }, [limpiar, marcar]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rename = async (id: number) => {
    const clean = editTitle.trim();
    setEditId(null);
    if (!clean) return;
    setBusy(id);
    try { await api.patch(`/api/recordings/${id}`, { title: clean }); setRows((rs) => (rs ?? []).map((x) => (x.id === id ? { ...x, title: clean } : x))); emitRecordingChange(id, null); }
    catch { r.notify({ kind: 'error' }); }
    finally { setBusy(null); }
  };
  const keep = async (id: number) => {
    setBusy(id);
    try { await api.post(`/api/recordings/${id}/keep`, {}); r.notify({ kind: 'kept' }); emitRecordingChange(id, null); load(); }
    catch { r.notify({ kind: 'error' }); }
    finally { setBusy(null); }
  };
  const del = async (id: number) => {
    if (!window.confirm(t('rec.deleteConfirm'))) return;
    setBusy(id);
    try { await api.post(`/api/recordings/${id}/delete`, {}); setRows((rs) => (rs ?? []).filter((x) => x.id !== id)); emitRecordingChange(id, null); if (r.recovery?.id === id) void r.discardRecovery(); }
    catch { r.notify({ kind: 'error' }); }
    finally { setBusy(null); }
  };
  const recover = async () => { setBusy(r.recovery?.id ?? null); try { await r.recover(); load(); } finally { setBusy(null); } };
  const discard = async (id: number) => {
    if (!window.confirm(t('rec.discardConfirm'))) return;
    setBusy(id);
    try { if (r.recovery?.id === id) await r.discardRecovery(); else await api.post(`/api/recordings/${id}/delete`, {}); setRows((rs) => (rs ?? []).filter((x) => x.id !== id)); }
    catch { r.notify({ kind: 'error' }); }
    finally { setBusy(null); }
  };
  const statusText = (x: RecordingDTO) => x.status === 'ready' ? t('rec.statusReady') : x.status === 'failed' ? t('rec.statusFailed') : x.status === 'uploading' ? t('rec.statusUploading') : t('rec.processing');

  return (
    <div className="ash-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ash-modal rec-mine" role="dialog" aria-modal="true" aria-label={t('rec.mine')}>
        <div className="ash-modal-head">
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Video size={17} strokeWidth={1.9} />{t('rec.mine')}</b>
          <button type="button" className="btn ghost sm ash-open" onClick={onClose} aria-label={t('common.close')}><X size={15} strokeWidth={1.75} /></button>
        </div>
        <div className="rec-mine-list">
          {rows === null && <div className="ss-empty">{t('common.loading')}</div>}
          {fallo ? <NoCargo onRetry={load} compact /> : rows !== null && !rows.length && <div className="ss-empty">{t('rec.mineEmpty')}</div>}
          {(rows ?? []).map((x) => {
            const unfinished = x.status === 'uploading';
            const canRecover = unfinished && r.recovery?.id === x.id && r.recovery.partsUploaded > 0;
            const dur = unfinished ? (r.recovery?.id === x.id ? fmtElapsed(r.recovery.recordedMs) : '—') : x.durationMs != null ? fmtElapsed(x.durationMs) : '—';
            return (
              <div key={x.id} className={`rec-mine-row${unfinished ? ' unfinished' : ''}`}>
                <div className="rec-mine-main">
                  {editId === x.id ? (
                    <span className="rec-mine-edit">
                      <input className="input sm" value={editTitle} autoFocus maxLength={140} onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void rename(x.id); if (e.key === 'Escape') setEditId(null); }} />
                      <button type="button" className="btn ghost sm" onClick={() => { void rename(x.id); }} aria-label={t('common.save')}><Check size={13} strokeWidth={2} /></button>
                    </span>
                  ) : (
                    <span className="rec-mine-title" title={x.title}>
                      {unfinished ? t('rec.unfinished', { d: dur }) : x.title}
                      {!unfinished && <button type="button" className="btn ghost sm rec-mine-pencil" onClick={() => { setEditId(x.id); setEditTitle(x.title); }} title={t('rec.rename')} aria-label={t('rec.rename')}><Pencil size={12} strokeWidth={1.75} /></button>}
                    </span>
                  )}
                  <span className="rec-card-meta">
                    <span className="ss-mono">{dur}</span>
                    <span>·</span>
                    <span>{fmtDate(x.createdAt, lang)}</span>
                    <span>·</span>
                    <span className={`rec-chip${x.status === 'failed' ? ' failed' : x.status === 'ready' ? ' ok' : ''}`}>{statusText(x)}</span>
                    {x.claim && <a className="rec-chip soft" href={`/claims/${x.claim.id}`} target="_blank" rel="noreferrer" title={t('rec.open')}>{t('rec.attached', { claim: claimLabel(x.claim) })}</a>}
                    {x.keepAt && <span className="rec-chip soft"><Bookmark size={10} strokeWidth={2} aria-hidden /> {t('rec.keep')}</span>}
                    {!x.keepAt && x.claimId == null && x.expiresAt && !unfinished && <span className="rec-chip soft">{t('rec.expires', { date: fmtDate(x.expiresAt, lang) })}</span>}
                    {x.sentTo > 0 && <span>{t('rec.sentTo', { n: x.sentTo })} · {t('rec.seenBy', { n: x.seenBy })}</span>}
                  </span>
                </div>
                <div className="rec-mine-actions">
                  {unfinished ? (
                    <>
                      {canRecover && <button type="button" className="btn sm" disabled={busy === x.id} onClick={() => { void recover(); }}>{t('rec.recover')}</button>}
                      <button type="button" className="btn ghost sm rec-del" disabled={busy === x.id} onClick={() => { void discard(x.id); }}>{t('rec.discard')}</button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn ghost sm" onClick={() => window.open(`/rec/${x.id}`, '_blank', 'noopener,popup,width=1000,height=640')} title={t('rec.open')}><AppWindow size={13} strokeWidth={1.75} /> {t('rec.open')}</button>
                      {x.claimId == null && !x.keepAt && x.status === 'ready' && <button type="button" className="btn ghost sm" disabled={busy === x.id} onClick={() => { void keep(x.id); }} title={t('rec.keepHint')}><Bookmark size={13} strokeWidth={1.75} /> {t('rec.keep')}</button>}
                      <button type="button" className="btn ghost sm rec-del" disabled={busy === x.id} onClick={() => { void del(x.id); }} title={t('rec.delete')} aria-label={t('rec.delete')}><Trash2 size={13} strokeWidth={1.75} /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
